// ============================================================================
// GESTIMMONY — Layout Portail Locataire (sidebar minimaliste)
// ============================================================================
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Home, Receipt, FileText, AlertTriangle, User, LogOut, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { LogoMark } from "@/components/Logo";

const NAV = [
  { to: "/portail", label: "Accueil", icon: Home, end: true, testid: "portal-nav-home" },
  { to: "/portail/quittances", label: "Mes quittances", icon: Receipt, testid: "portal-nav-receipts" },
  { to: "/portail/regularisations", label: "Régularisations", icon: Scale, testid: "portal-nav-regularisations" },
  { to: "/portail/bail", label: "Mon bail", icon: FileText, testid: "portal-nav-lease" },
  { to: "/portail/incidents", label: "Signaler un problème", icon: AlertTriangle, testid: "portal-nav-incidents" },
  { to: "/portail/courriers", label: "Mes courriers", icon: FileText, testid: "portal-nav-letters" },
  { to: "/portail/profil", label: "Mon profil", icon: User, testid: "portal-nav-profile" },
];

export default function PortalLayout() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/portal/me")
      .then(({ data }) => setMe(data))
      .catch(() => navigate("/portail/login"))
      .finally(() => setLoading(false));
  }, [navigate]);

  const logout = async () => {
    try { await api.post("/portal/logout"); } catch {}
    navigate("/portail/login");
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Chargement…</div>;
  if (!me) return null;

  const initials = `${(me.first_name || "?").charAt(0)}${(me.last_name || "").charAt(0)}`.toUpperCase();

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="hidden md:flex md:w-64 md:flex-col border-r border-slate-200 bg-white" data-testid="portal-sidebar">
        <div className="flex h-16 items-center gap-2 px-6 border-b border-slate-200">
          <LogoMark className="h-8" />
          <div>
            <div className="font-heading font-bold text-slate-900 tracking-tight leading-tight">Gestimmony</div>
            <div className="text-[10px] uppercase tracking-wider text-teal-700 font-semibold">Locataire</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-sm ${
                  isActive ? "bg-teal-50 text-teal-700 font-semibold" : "text-slate-700 hover:bg-slate-50"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="text-xs text-slate-500 mb-2">Bailleur : <b>{me.landlord.name}</b></div>
          <Button data-testid="portal-logout" onClick={logout} variant="outline" size="sm" className="w-full">
            <LogOut className="h-3.5 w-3.5 mr-2" /> Déconnexion
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 shrink-0 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-8 sticky top-0 z-30">
          <div className="md:hidden font-heading font-bold text-slate-900">Gestimmony Locataire</div>
          <div className="hidden md:block text-sm text-slate-500">
            Bonjour <span className="font-medium text-slate-900">{me.first_name} {me.last_name}</span>
          </div>
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 text-white flex items-center justify-center text-xs font-bold">
            {initials || "?"}
          </div>
        </header>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 flex justify-around py-2">
          {NAV.slice(0, 5).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] ${
                  isActive ? "text-teal-700" : "text-slate-500"
                }`
              }>
              <n.icon className="h-4 w-4" />
              {n.label.split(" ")[0]}
            </NavLink>
          ))}
        </nav>

        <main className="flex-1 p-4 md:p-8 pb-24 md:pb-8 min-w-0">
          <Outlet context={{ me }} />
        </main>
      </div>

      <Toaster richColors position="top-right" />
    </div>
  );
}
